import { jsPDF } from 'jspdf';
import { Application } from '../types';
import { generateSecurityCode } from './helpers';

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
};

const compressImage = (
  img: HTMLImageElement,
  format: 'JPEG' | 'PNG',
  maxDim: number = 800,
  quality: number = 0.6
): string => {
  const canvas = document.createElement('canvas');
  let width = img.width;
  let height = img.height;

  if (width > maxDim || height > maxDim) {
    if (width > height) {
      height = Math.round((height * maxDim) / width);
      width = maxDim;
    } else {
      width = Math.round((width * maxDim) / height);
      height = maxDim;
    }
  }

  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    if (format === 'JPEG') {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(img, 0, 0, width, height);
  }
  
  const mimeType = format === 'JPEG' ? 'image/jpeg' : 'image/png';
  return canvas.toDataURL(mimeType, format === 'JPEG' ? quality : undefined);
};

export const generateConfirmationLetter = async (application: Application): Promise<jsPDF> => {
  const doc = new jsPDF('p', 'mm', 'a4');

  const width = doc.internal.pageSize.getWidth();
  const margin = 20;

  const [rawLetterhead, rawSignature, rawSeal, rawPhoto] = await Promise.all([
    loadImage('/Letterpad.jpg.jpeg'),
    loadImage('/assets/letters/signature.png'),
    loadImage('/assets/letters/seal.png'),
    application.documents?.photo ? loadImage(application.documents.photo).catch(() => null) : null
  ]);

  const letterheadImg = compressImage(rawLetterhead, 'JPEG', 1000, 0.65);
  const signatureImg = compressImage(rawSignature, 'PNG', 300);
  const sealImg = compressImage(rawSeal, 'PNG', 300);
  const photoImg = rawPhoto ? compressImage(rawPhoto, 'JPEG', 300, 0.6) : null;

  const addLetterhead = (pageDoc: jsPDF) => {
    const pW = pageDoc.internal.pageSize.getWidth();
    const pH = pageDoc.internal.pageSize.getHeight();
    pageDoc.addImage(letterheadImg, 'JPEG', 0, 0, pW, pH);
  };

  const securityCode = generateSecurityCode(application.id);
  const fullName = `${application.firstName} ${application.lastName}`.toUpperCase();
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // --- PAGE 1: INTRODUCTION & DETAILS ---
  addLetterhead(doc);
  
  // Header Meta Row
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Reg No: ${application.registrationNumber || application.id.replace(/-/g, '/')}`, margin, 47);
  doc.text(`Security Code: ${securityCode}`, width / 2, 47, { align: 'center' });
  doc.text(`Date: ${today}`, width - margin, 47, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('TRAINING CONFIRMATION LETTER', width / 2, 55, { align: 'center' });
  doc.line(width / 2 - 45, 57, width / 2 + 45, 57);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('To,', margin, 75);
  doc.text(`${fullName}`, margin, 82);
  doc.setFont('helvetica', 'normal');
  doc.text(`${application.address.village}, ${application.address.block}`, margin, 87);
  doc.text(`${application.address.district}, ${application.address.state} - ${application.address.pincode}`, margin, 92);

  doc.setFont('helvetica', 'bold');
  doc.text(`Subject: Confirmation of Enrollment & Kreed Odisha Training Programme.`, margin, 105);

  // Add Photo if available
  if (photoImg) {
    const photoX = width - margin - 35;
    const photoY = 70; 
    const photoW = 35;
    const photoH = 45;
    doc.setDrawColor(180);
    doc.rect(photoX, photoY, photoW, photoH);
    doc.addImage(photoImg, 'JPEG', photoX + 1, photoY + 1, photoW - 2, photoH - 2);
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const intro = `Dear ${application.firstName},\n\nWe are pleased to inform you that your application for participation in the Kreed Odisha Training Program has been successfully accepted and confirmed.\n\nYour selection reflects your eligibility, commitment, and potential to contribute to the development of Odisha's sports and educational ecosystem. The Kreed Odisha Program, organized and conducted by Hardini Dynamics Pvt. Ltd., focuses on empowering sports instructors, coaches, and athletes through structured training, modern methodology, and leadership development initiatives.`;
  const splitIntro = doc.splitTextToSize(intro, width - (margin * 2));
  doc.text(splitIntro, margin, 115);

  let currentY = 180;
  doc.setFont('helvetica', 'bold');
  doc.text('Certification:', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const certText = `Upon successful completion of the training and fulfillment of all requirements (attendance, performance, and participation), you will receive an Official Certificate of Completion from Hardini Dynamics Pvt. Ltd.`;
  const splitCert = doc.splitTextToSize(certText, width - (margin * 2));
  doc.text(splitCert, margin, currentY + 5);

  currentY += 25;
  doc.setFont('helvetica', 'bold');
  doc.text('Training Details:', margin, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text('• Program Title    : Kreed Odisha Training Program', margin + 5, currentY + 7);
  doc.text('• Training Mode : Offline', margin + 5, currentY + 14);
  doc.text('• Duration           : 21 days', margin + 5, currentY + 21);

  // --- PAGE 2: TERMS & CONDITIONS ---
  doc.addPage();
  addLetterhead(doc);

  currentY = 55;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('TERMS AND CONDITIONS', width / 2, currentY, { align: 'center' });
  
  doc.setFontSize(11);
  currentY += 15;
  doc.text('1. Code of Conduct', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const coc1 = '– All participants must maintain professional behavior, punctuality, and respect toward peers and instructors.';
  const coc2 = '– Any form of misconduct or rule violation will result in immediate removal from the program.';
  const splitCoc1 = doc.splitTextToSize(coc1, width - (margin * 2 + 10));
  doc.text(splitCoc1, margin + 5, currentY + 7);
  currentY += (splitCoc1.length * 6) + 5;
  const splitCoc2 = doc.splitTextToSize(coc2, width - (margin * 2 + 10));
  doc.text(splitCoc2, margin + 5, currentY);

  currentY += (splitCoc2.length * 6) + 5;
  doc.setFont('helvetica', 'bold');
  doc.text('2. Medical Fitness', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const med1 = '– Participants must provide a valid medical fitness certificate before the start of training.';
  const med2 = '– Annual fitness evaluations will be required for continued participation in the Kreed Odisha Program.';
  const splitMed1 = doc.splitTextToSize(med1, width - (margin * 2 + 10));
  doc.text(splitMed1, margin + 5, currentY + 7);
  currentY += (splitMed1.length * 6) + 5;
  const splitMed2 = doc.splitTextToSize(med2, width - (margin * 2 + 10));
  doc.text(splitMed2, margin + 5, currentY);

  currentY += (splitMed2.length * 6) + 5;
  doc.setFont('helvetica', 'bold');
  doc.text('3. Modifications', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const mod1 = '– Hardini Dynamics reserves the right to revise program schedules, structure, and terms based on organizational needs or unforeseen circumstances.';
  const splitMod1 = doc.splitTextToSize(mod1, width - (margin * 2 + 10));
  doc.text(splitMod1, margin + 5, currentY + 7);

  currentY += (splitMod1.length * 6) + 10;
  doc.setFont('helvetica', 'bold');
  doc.text('4. Confidentiality', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const conf1 = `Confidential Nature of Association: Participation in the Kreed Odisha Program is confidential in nature. Participants shall not disclose, discuss, or share any internal information, operational details, or materials related to the program, its structure, or affiliated organizations with any unauthorized person during or after their association with Hardini Dynamics Pvt. Ltd.`;
  const splitConf1 = doc.splitTextToSize(conf1, width - (margin * 2 + 5));
  doc.text(splitConf1, margin + 5, currentY + 7);

  currentY += (splitConf1.length * 6) + 10;
  const conf2 = `Restriction on Communication and Publication: No participant is permitted to publish, post, circulate, or communicate any training materials, reports, documents, or digital resources related to the Kreed Odisha Program without the prior written approval of Hardini Dynamics management.`;
  const splitConf2 = doc.splitTextToSize(conf2, width - (margin * 2 + 5));
  doc.text(splitConf2, margin + 5, currentY);

  currentY += (splitConf2.length * 6) + 10;
  const conf3 = `Information Security and Data Protection: Participants must strictly follow the data protection, digital communication, and information security policies of Hardini Dynamics Pvt. Ltd. Any unauthorized access, misuse, or sharing of data or digital platforms will be treated as a serious breach of confidentiality.`;
  const splitConf3 = doc.splitTextToSize(conf3, width - (margin * 2 + 5));
  doc.text(splitConf3, margin + 5, currentY);

  currentY += (splitConf3.length * 6) + 10;
  const conf4 = `Ownership and Intellectual Property: All documents, study materials, training modules, and resources provided under the Kreed Odisha Program are the exclusive intellectual property of Hardini Dynamics Pvt. Ltd. Participants must handle all materials responsibly and must not reproduce, share, or use them for any external purpose.`;
  const splitConf4 = doc.splitTextToSize(conf4, width - (margin * 2 + 5));
  doc.text(splitConf4, margin + 5, currentY);

  // --- PAGE 3: CONDUCT & LEGAL ---
  doc.addPage();
  addLetterhead(doc);

  currentY = 55;
  doc.setFont('helvetica', 'bold');
  doc.text('Conduct and Professional Behavior:', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const conductText = `Participants are expected to maintain the highest standards of professionalism, discipline, and ethical behavior. Any act of misbehavior, misconduct, or violation of program decorum — whether during training or official representation — will lead to immediate debarment from the program and cancellation of certification or renewal rights.`;
  const splitConduct = doc.splitTextToSize(conductText, width - (margin * 2));
  doc.text(splitConduct, margin, currentY + 7);

  currentY += (splitConduct.length * 6) + 10;
  doc.setFont('helvetica', 'bold');
  doc.text('Consequences of Breach:', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const breachText = `Any breach of confidentiality, misuse of confidential data, violation of communication policies, or act of misconduct will result in disciplinary action, including suspension, permanent debarment, or legal proceedings under applicable Indian laws.`;
  const splitBreach = doc.splitTextToSize(breachText, width - (margin * 2));
  doc.text(splitBreach, margin, currentY + 7);

  currentY += (splitBreach.length * 6) + 10;
  doc.setFont('helvetica', 'bold');
  doc.text('5. Jurisdiction', margin, currentY);
  doc.setFont('helvetica', 'normal');
  const jurText = '– All legal matters arising from this program shall be subject to the jurisdiction of the competent courts in Cuttack, Odisha, India.';
  const splitJur = doc.splitTextToSize(jurText, width - (margin * 2 + 10));
  doc.text(splitJur, margin + 5, currentY + 7);

  currentY += (splitJur.length * 6) + 15;
  const closingText = `We congratulate you once again on your selection and look forward to your enthusiastic participation in this initiative that envisions Odisha as a global hub of sporting excellence. After completion of the Kreed Odisha training program, you may be offered suitable positions in the field of physical education in various schools and other educational institutions based on qualifications, availability, and program evaluation.\n\nPlease sign and return a copy of this letter, duly countersigned on each page, indicating your acceptance of the terms and conditions outlined.\n\nFor any further assistance or clarification, please contact us at hardinidynamics@gmail.com or visit https://okreed.com`;
  const splitClosing = doc.splitTextToSize(closingText, width - (margin * 2));
  doc.text(splitClosing, margin, currentY);

  currentY += (splitClosing.length * 6) + 15;
  doc.text('Warm regards,', margin, currentY);
  doc.addImage(signatureImg, 'PNG', margin, currentY + 2, 40, 15);
  doc.addImage(sealImg, 'PNG', margin + 45, currentY + 2, 25, 25);
  doc.setFont('helvetica', 'bold');
  doc.text('For Hardini Dynamics Pvt. Ltd.', margin, currentY + 22);

  return doc;
};

export const generateScheduleLetter = async (application: Application): Promise<jsPDF> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const width = doc.internal.pageSize.getWidth();
  const margin = 20;

  const [rawLetterhead, rawSignature, rawSeal] = await Promise.all([
    loadImage('/Letterpad.jpg.jpeg'),
    loadImage('/assets/letters/signature.png'),
    loadImage('/assets/letters/seal.png'),
  ]);

  const letterheadImg = compressImage(rawLetterhead, 'JPEG', 1000, 0.65);
  const signatureImg = compressImage(rawSignature, 'PNG', 300);
  const sealImg = compressImage(rawSeal, 'PNG', 300);

  const addLetterhead = (pageDoc: jsPDF) => {
    const pW = pageDoc.internal.pageSize.getWidth();
    const pH = pageDoc.internal.pageSize.getHeight();
    pageDoc.addImage(letterheadImg, 'JPEG', 0, 0, pW, pH);
  };

  const securityCode = generateSecurityCode(application.id);
  const fullName = `${application.firstName} ${application.lastName}`.toUpperCase();
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });

  addLetterhead(doc);

  // Header Meta Row
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Reg No: ${application.registrationNumber || application.id.replace(/-/g, '/')}`, margin, 47);
  doc.text(`Security Code: ${securityCode}`, width / 2, 47, { align: 'center' });
  doc.text(`Date: ${today}`, width - margin, 47, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('TRAINING SCHEDULE LETTER', width / 2, 55, { align: 'center' });
  doc.line(width / 2 - 40, 57, width / 2 + 40, 57);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('To,', margin, 75);
  doc.text(`${fullName}`, margin, 82);

  doc.setFont('helvetica', 'bold');
  doc.text(`Subject: Detailed Training Schedule for Kreed Odisha Program.`, margin, 100);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text('Dear Candidate,', margin, 112);
  doc.text('The following is your assigned training schedule and batch details:', margin, 119);

  let currentY = 130;
  // Table Header
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, currentY, width - (margin * 2), 10, 'F');
  doc.setFont('helvetica', 'bold');
  doc.text('Event / Phase', margin + 5, currentY + 7);
  doc.text('Date / Timeline', margin + 100, currentY + 7);

  doc.setFont('helvetica', 'normal');
  currentY += 10;
  doc.rect(margin, currentY, width - (margin * 2), 10);
  doc.text('Reporting & Induction', margin + 5, currentY + 7);
  doc.text('Day 01', margin + 100, currentY + 7);

  currentY += 10;
  doc.rect(margin, currentY, width - (margin * 2), 10);
  doc.text('Phase 1: Fundamental Training', margin + 5, currentY + 7);
  doc.text('Day 02 - Day 07', margin + 100, currentY + 7);

  currentY += 10;
  doc.rect(margin, currentY, width - (margin * 2), 10);
  doc.text('Phase 2: Advanced Methodology', margin + 5, currentY + 7);
  doc.text('Day 08 - Day 15', margin + 100, currentY + 7);

  currentY += 10;
  doc.rect(margin, currentY, width - (margin * 2), 10);
  doc.text('Phase 3: Assessment & Evaluation', margin + 5, currentY + 7);
  doc.text('Day 16 - Day 20', margin + 100, currentY + 7);

  currentY += 10;
  doc.rect(margin, currentY, width - (margin * 2), 10);
  doc.text('Certification Ceremony', margin + 5, currentY + 7);
  doc.text('Day 21', margin + 100, currentY + 7);

  currentY += 25;
  doc.setFont('helvetica', 'bold');
  doc.text('Important Instructions:', margin, currentY);
  doc.setFont('helvetica', 'normal');
  doc.text('1. Reporting time is 09:00 AM sharp at the designated venue.', margin + 5, currentY + 7);
  doc.text('2. Please bring this schedule letter and your Aadhaar Card.', margin + 5, currentY + 14);
  doc.text('3. Uniform/Dress code: Sports tracks and training shoes.', margin + 5, currentY + 21);

  currentY += 40;
  doc.text('Warm regards,', margin, currentY);
  doc.addImage(signatureImg, 'PNG', margin, currentY + 2, 40, 15);
  doc.addImage(sealImg, 'PNG', margin + 45, currentY + 2, 25, 25);
  doc.setFont('helvetica', 'bold');
  doc.text('Hardini Dynamics Pvt. Ltd.', margin, currentY + 22);

  return doc;
};
